import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { ErrorContext, ApiResponse } from '../types';
import { translateError } from '../utils/errorMessages';

interface ErrorHandlerContextType {
    handleError: (error: any, module: string, action: string, metadata?: Record<string, any>) => ApiResponse;
    getUserFriendlyMessage: (error: any) => string;
    handleApiError: (error: any, method: string, endpoint: string) => ApiResponse;
    handleAuthError: (error: any, action: string) => ApiResponse;
    handleStorageError: (error: any, action: string, metadata?: Record<string, any>) => ApiResponse;
    handlePushNotificationError: (error: any, action: string) => ApiResponse;
}

export const ErrorHandlerContext = createContext<ErrorHandlerContextType | undefined>(undefined);

interface ErrorHandlerProviderProps {
    children: ReactNode;
    onError?: (error: Error, context: ErrorContext) => void;
}

export const ErrorHandlerProvider: React.FC<ErrorHandlerProviderProps> = ({ 
    children, 
    onError 
}) => {
    // Log error helper
    const logError = useMemo(() => {
        return (error: any, context: ErrorContext) => {
            const errorMessage = error.message || error.toString();
            const errorStack = error.stack || 'No stack trace available';

            console.error(`[${context.module}] ${context.action}:`, {
                error: errorMessage,
                // Technical cause (URL, timeout, raw fetch message) - logged, never shown.
                detail: error.detail,
                stack: errorStack,
                context,
            });

            // Call custom error handler if provided
            if (onError) {
                onError(error instanceof Error ? error : new Error(errorMessage), context);
            }
        };
    }, [onError]);

    // Handle API errors
    const handleApiError = (error: any, method: string, endpoint: string): ApiResponse => {
        const context: ErrorContext = {
            module: 'communication',
            action: `${method} ${endpoint}`,
            timestamp: Date.now(),
        };

        logError(error, context);

        // Whatever comes back here is rendered to the user, so it must never carry the
        // request URL, a status line or a stack trace - only a translated generic message.
        if (error?.name === 'NetworkError' || (error?.name === 'TypeError' && error?.message?.includes('Network request failed'))) {
            return {
                success: false,
                error: translateError('network'),
            };
        }

        if (error?.name === 'AbortError' || error?.name === 'TimeoutError') {
            return {
                success: false,
                error: translateError('timeout'),
            };
        }

        return {
            success: false,
            error: translateError('generic'),
        };
    };

    // Handle authentication errors
    const handleAuthError = (error: any, action: string): ApiResponse => {
        const context: ErrorContext = {
            module: 'auth',
            action,
            timestamp: Date.now(),
        };

        logError(error, context);

        if (error.message?.includes('Invalid credentials')) {
            return {
                success: false,
                error: 'Invalid email or password',
            };
        }

        if (error.message?.includes('User not found')) {
            return {
                success: false,
                error: 'No account found with this email',
            };
        }

        if (error.message?.includes('Token expired')) {
            return {
                success: false,
                error: 'Session expired. Please log in again.',
            };
        }

        return {
            success: false,
            error: error.message || 'Authentication failed',
        };
    };

    // Handle storage errors
    const handleStorageError = (error: any, action: string, metadata?: Record<string, any>): ApiResponse => {
        const context: ErrorContext = {
            module: 'storage',
            action,
            timestamp: Date.now(),
            metadata,
        };

        logError(error, context);

        if (error.message?.includes('Storage quota exceeded')) {
            return {
                success: false,
                error: 'Storage quota exceeded. Please free up some space.',
            };
        }

        if (error.message?.includes('Key not found')) {
            return {
                success: false,
                error: 'Data not found',
            };
        }

        return {
            success: false,
            error: error.message || 'Storage operation failed',
        };
    };

    // Handle push notification errors
    const handlePushNotificationError = (error: any, action: string): ApiResponse => {
        const context: ErrorContext = {
            module: 'push-notifications',
            action,
            timestamp: Date.now(),
        };

        logError(error, context);

        if (error.message?.includes('Permission denied')) {
            return {
                success: false,
                error: 'Notification permission denied. Please enable notifications in settings.',
            };
        }

        if (error.message?.includes('Token not available')) {
            return {
                success: false,
                error: 'Push notification token not available',
            };
        }

        return {
            success: false,
            error: error.message || 'Push notification operation failed',
        };
    };

    // Handle general errors
    const handleError = (error: any, module: string, action: string, metadata?: Record<string, any>): ApiResponse => {
        const context: ErrorContext = {
            module,
            action,
            timestamp: Date.now(),
            metadata,
        };

        logError(error, context);

        return {
            success: false,
            error: error.message || 'An unexpected error occurred',
        };
    };

    // Create user-friendly error messages
    const getUserFriendlyMessage = (error: any): string => {
        if (error?.name === 'NetworkError' || error?.message?.includes('Network request failed')) {
            return translateError('network');
        }

        if (error?.name === 'TimeoutError' || error?.name === 'AbortError' || error?.message?.includes('timeout')) {
            return translateError('timeout');
        }

        if (error?.message?.includes('Internal Server Error')) {
            return translateError('server');
        }

        return translateError('generic');
    };

    const value: ErrorHandlerContextType = {
        handleError,
        getUserFriendlyMessage,
        handleApiError,
        handleAuthError,
        handleStorageError,
        handlePushNotificationError,
    };

    return (
        <ErrorHandlerContext.Provider value={value}>
            {children}
        </ErrorHandlerContext.Provider>
    );
};

export const useErrorHandler = (): ErrorHandlerContextType => {
    const context = useContext(ErrorHandlerContext);
    if (context === undefined) {
        throw new Error('useErrorHandler must be used within an ErrorHandlerProvider');
    }
    return context;
};
